// @flow

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
	DataGridPremium,
	GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
	GridActionsCellItem,
	GridToolbar
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageMenu from "../../../components/custom/app/pageMenu/pageMenu";
import PageTitle from "../../../components/mui/app/pageTitle/pageTitle";
import PageContentWrapper from "../../../components/mui/general/pageContentWrapper/pageContentWrapper";
import { CustomPagination } from "../../../components/mui/grids/dataGridCustom/customPagination";
import DataGridWrapper from "../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { muiGridLocales } from "../../../helpers/mui/muiLocales";
import { access_registrations, access_user } from "../../../helpers/rights";
import { getRouteUrl } from "../../../helpers/routing/getRouteUrl";
import { useMuiGridServerSide } from "../../../hooks/muiGrid/useMuiGridServerSide";
import useUserData from "../../../hooks/user/useUserData";
import { withPermissionHOC } from "../../permission/components/permissionHOC/permissionHOC";
import { REGISTRATIONS } from "../locales/namespaces";
import { ROUTE_PAGE_REGISTRATION_EDIT } from "../routes/routes";

type Props = {

};

const Index = (props: Props) => {
	const { t } = useTranslation(REGISTRATIONS);
	useEffect(() => {
		document.title = t('Registrations List')
	}, [])
	
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange
	} = useMuiGridServerSide({
		url: 'fts/project/work/index',
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }]
	})
	
	const { language, dateFormat, roles} = useUserData()

	const columns = useMemo(() => {
		return [
			{
				headerName: t('Actions'),
				field: 'actions',
				type: 'actions',
				getActions: (params) => {
					if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
						return []
					}
					return [
						<GridActionsCellItem
							component={Link}
							to={getRouteUrl(ROUTE_PAGE_REGISTRATION_EDIT, { registrationId: params.id })}
							icon={
								<Tooltip title={t('Edit')}>
									<IconButton>
										<EditIcon/>
									</IconButton>
								</Tooltip>
							}
							color={'info'}
							label={t('Edit')}>
						</GridActionsCellItem>
					]
				}
			},
			...(Number(roles?.[0]) !== 2
				? [{
					field: 'Foreman',
					headerName: t('Foreman'),
					minWidth: 300,
					flex: 1,
				}]
				: []),
			{
				field: 'Description',
				headerName: t('Project'),
				minWidth: 300,
				flex: 1,
			},
			{
				field: 'WorkDt',
				headerName: t('WorkDt'),
				minWidth: 180, flex: 1,
				align:'center',
				valueFormatter: (value, row, column, apiRef) => {
					return (value ? dayjs(value).format(dateFormat) : null)
				},
				valueGetter: (value, row, column, apiRef) => {
					return value && new Date(value)
				}
			},
			{
				field: 'WorkTm',
				type: 'number',
				headerName: t('Total Work Time'),
				minWidth: 160, flex: 1,
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			},
			{
				field: 'ExtraWorkTm',
				type: 'number',
				headerName: t('Total Extra Work Time'),
				minWidth: 160, flex: 1,
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			}
		
		]
	}, [t, dateFormat])
	
	return (
		<Box className={'registrations_index'} sx={{ marginTop: '10px' }}>
			
			<PageMenu>
				<Button
					component={Link}
					to={'/registration/create'}
					startIcon={<AddCircleOutlineIcon/>}
				>
					{t('Add')}
				</Button>
			
			</PageMenu>
			<PageContentWrapper>
				<PageTitle
					title={t('Registrations')}/>
				<DataGridWrapper sx={{ height: 700 }}>
					<DataGridPremium
						columns={columns}
						initialState={{ density: "compact" }}
						rows={data}
						pagination
						paginationModel={{ page, pageSize }}
						paginationMode="server"
						onPaginationModelChange={onPaginationModelChange}
						rowCount={Number(total)}
						pageSizeOptions={[5, 10, 15, 20, 50, 100]}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
						filterMode="server"
						onFilterModelChange={onFilterModelChange}
						loading={isLoading}
						localeText={muiGridLocales(language)}
						slots={{
							pagination: CustomPagination,
							// toolbar: GridToolbar,
						}}
						disableColumnFilter
						headerFilters
					/>
				</DataGridWrapper>
			</PageContentWrapper>
		</Box>
	);
};

// export default Index
export default withPermissionHOC(Index, [access_registrations])